import { useEffect, useState } from 'react';
import { PreworkDetailCardsList } from '../PreworkDetailCardList';
import StackedHorizontalBar from '../../../components/StackedHorizontalBar';
import { FilterGraphsPreWork } from './FilterGraphs';
import { useGraphViewPrework } from './useGraphView';
import { ChartData } from 'chart.js';
import { useTranslation } from 'react-i18next';

export const PreworkGraphicalView = ({
	selectedBatchId,
}: {
	selectedBatchId: number | undefined;
}) => {
	const { t } = useTranslation();
	const { graphViewData, graphViewFilter, onGraphViewFilterChange } =
		useGraphViewPrework();
	const [regionGroupData, setRegionGroupData] =
		useState<ChartData<'bar'> | null>();
	const [regionMentorData, setRegionMentorData] =
		useState<ChartData<'bar'> | null>();
	const [zoneGroupData, setZoneGroupData] = useState<ChartData<'bar'> | null>();
	const [zoneMentorData, setZoneMentorData] =
		useState<ChartData<'bar'> | null>();

	useEffect(() => {
		const regionData = graphViewData?.regionWisePreworkGraphicalView;
		if (!regionData) {
			return;
		}
		const labels = regionData.map((view) => view.region);
		const mappedToGroupData = regionData.map((view) =>
			Number(view.candidateMappedWithGroupCount || '0')
		);
		const notMappedToGroupData = regionData.map((view) =>
			Number(view.candidateNotMappedWithGroupCount || '0')
		);
		const data = {
			labels,
			datasets: [
				{
					label: t('mappedToGroup'),
					data: mappedToGroupData,
					backgroundColor: 'rgba(71, 145, 255)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
				{
					label: t('notMappedToGroup'),
					data: notMappedToGroupData,
					backgroundColor: 'rgba(2, 186, 177)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
			],
		};
		setRegionGroupData(data);
	}, [graphViewData?.regionWisePreworkGraphicalView, t]);

	useEffect(() => {
		const regionData = graphViewData?.regionWisePreworkGraphicalView;
		if (!regionData) {
			return;
		}
		const labels = regionData.map((view) => view.region);
		const mappedToMentorData = regionData.map((view) =>
			Number(view.candidateAssignedWithMentorCount || '0')
		);
		const notMappedToMentorData = regionData.map((view) =>
			Number(view.candidateNotAssignedWithMentorCount || '0')
		);
		const data = {
			labels,
			datasets: [
				{
					label: t('mappedWithMentors'),
					data: mappedToMentorData,
					backgroundColor: 'rgba(219, 31, 88)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
				{
					label: t('notMappedWithMentors'),
					data: notMappedToMentorData,
					backgroundColor: 'rgba(255, 217, 80)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
			],
		};
		setRegionMentorData(data);
	}, [graphViewData?.regionWisePreworkGraphicalView, t]);

	useEffect(() => {
		const zoneData = graphViewData?.zoneWisePreworkGraphicalView;
		if (!zoneData) {
			return;
		}
		const labels = zoneData.map((view) => view.zone);
		const mappedToGroupData = zoneData.map((view) =>
			Number(view.candidateMappedWithGroupCount || '0')
		);
		const notMappedToGroupData = zoneData.map((view) =>
			Number(view.candidateNotMappedWithGroupCount || '0')
		);
		const data = {
			labels,
			datasets: [
				{
					label: t('mappedToGroup'),
					data: mappedToGroupData,
					backgroundColor: 'rgba(71, 145, 255)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
				{
					label: t('notMappedToGroup'),
					data: notMappedToGroupData,
					backgroundColor: 'rgba(2, 186, 177)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
			],
		};
		setZoneGroupData(data);
	}, [graphViewData?.zoneWisePreworkGraphicalView, t]);

	useEffect(() => {
		const zoneData = graphViewData?.zoneWisePreworkGraphicalView;
		if (!zoneData) {
			return;
		}
		const labels = zoneData.map((view) => view.zone);
		const mappedToMentorData = zoneData.map((view) =>
			Number(view.candidateAssignedWithMentorCount || '0')
		);
		const notMappedToMentorData = zoneData.map((view) =>
			Number(view.candidateNotAssignedWithMentorCount || '0')
		);
		const data = {
			labels,
			datasets: [
				{
					label: t('mappedWithMentors'),
					data: mappedToMentorData,
					backgroundColor: 'rgba(219, 31, 88)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
				{
					label: t('notMappedWithMentors'),
					data: notMappedToMentorData,
					backgroundColor: 'rgba(255, 217, 80)',
					borderColor: 'lightGray',
					borderWidth: 1,
					barThickness: 40,
				},
			],
		};
		setZoneMentorData(data);
	}, [graphViewData?.zoneWisePreworkGraphicalView, t]);

	return (
		<>
			<FilterGraphsPreWork
				value={graphViewFilter}
				onFilterChange={onGraphViewFilterChange}
				selectedBatchId={selectedBatchId}
			/>
			<PreworkDetailCardsList
				dashboardDetail={graphViewData?.preworkDashboardDetail}
			/>
			{regionGroupData && !graphViewFilter.regionIds?.length && (
				<StackedHorizontalBar
					key={`regionGroup-${JSON.stringify(regionGroupData)}`}
					title={t('groupHeadingForGraphPrework')}
					data={regionGroupData}
					displayPercentage
				/>
			)}
			{regionMentorData && !graphViewFilter.regionIds?.length && (
				<StackedHorizontalBar
					key={`regionMentor-${JSON.stringify(regionMentorData)}`}
					title={t('mentorHeadingForGraphPrework')}
					data={regionMentorData}
					displayPercentage
				/>
			)}
			{zoneGroupData && graphViewFilter.regionIds?.length ? (
				<StackedHorizontalBar
					key={`zoneGroup-${JSON.stringify(zoneGroupData)}`}
					title={t('groupHeadingForGraphPrework')}
					data={zoneGroupData}
					displayPercentage
				/>
			) : null}
			{zoneMentorData && graphViewFilter.regionIds?.length ? (
				<StackedHorizontalBar
					key={`zoneMentor-${JSON.stringify(zoneMentorData)}`}
					title={t('mentorHeadingForGraphPrework')}
					data={zoneMentorData}
					displayPercentage
				/>
			) : null}
		</>
	);
};
